<template>
  <button
    v-tooltip.top="`AI Generate`"
    class="border rounded px-2 flex items-center h-8 cursor-pointer hover:bg-gray-100 bg-white"
    @click="showAIModal = true"
  >
    <Icon
      class="h-[20px] w-[30px]"
      name="hugeicons:artificial-intelligence-02"
    />
    AI Writer
  </button>

  <Dialog
    v-model:visible="showAIModal"
    header=" "
    modal
  >
    <div class="flex flex-col gap-4 w-[40rem]">
      <section class="border rounded p-3">
        <div class="">
          <span class="font-medium block mb-2">AI Writer</span>

          <div class="p-2 h-48 flex items-center justify-center">
            <span v-if="!response">
              <div
                v-if="isThinking"
                class="grid-cols-1"
              >
                <div class="flex justify-center">
                  <Icon
                    name="eos-icons:three-dots-loading"
                    class="h-10 w-10"
                  />
                </div>
                <div class="flex justify-center">
                  <span v-if="model == 'sonar-deep-research'">
                    Let me take 2-3 minutes to search for real-time resources and write something for you.
                  </span>
                  <span v-else>
                    Let me think... it might take up to 1 minute to generate an answer
                  </span>
                </div>
              </div>
              <div
                v-else
                class="grid-cols-1"
              >
                <div class="flex justify-center">
                  <Icon
                    name="hugeicons:artificial-intelligence-02"
                    class="h-10 w-10"
                  />
                </div>
                <div class="flex justify-center mt-4">
                  <span>What can I help with?</span>
                </div>
              </div>
            </span>

            <div
              v-else
              class="h-full overflow-y-scroll whitespace-pre-line"
              v-html="response"
            />
          </div>
        </div>
        <div>
          <div class="w-full grid grid-cols-1 border rounded hover:border-gray-300">
            <div>
              <textarea
                v-model="prompt"
                placeholder="Ask anything about your business sales writing..."
                class="w-full min-h-32 bg-transparent border-none outline-none resize-none focus:ring-0 p-3"
                :class="[{ 'bg-gray-100': isThinking }]"
                :disabled="isThinking"
              />
            </div>
            <div class="w-full flex justify-between p-3 border-t">
              <div>
                <Button
                  size="small"
                  :disabled="isThinking"
                  @click="toggle"
                >
                  Quick Help
                  <Icon name="mdi:lightbulb-on-10" />
                </Button>
              </div>
              <div>
                <Button
                  size="small"
                  class="w-32"
                  label="Write"
                  :disabled="isThinking"
                  @click="submit()"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </Dialog>

  <Popover ref="op">
    <div
      v-auto-animate
      class="grid grid-cols-1 w-[25rem] divide-y"
    >
      <div
        v-for="aiOption in computedAIOptions"
        :key="aiOption.id"
        @click="setActiveAIPrompt(aiOption)"
      >
        <div class="flex relative hover:bg-gray-100 cursor-pointer py-2 px-1">
          Write {{ aiOption.name }}
          <span class="absolute right-0">
            <Icon name="material-symbols:chevron-right" />
          </span>
        </div>
      </div>
    </div>
  </Popover>
</template>

<script setup lang="ts">
import { aiOptionsData } from './AIGenerateData'
import { useEventBus, EventNames } from '~/composables/useEventBus'

const editor = defineModel('editor')
const data = defineModel('data')

const prompt = ref('')
const maxToken = ref(1000)
const model = ref('sonar-pro')

const response = ref('')
const isThinking = ref(false)

const op = ref()
const toggle = (event) => {
  op.value.toggle(event)
}
const showAIModal = ref(false)
// GET Business
const { getBusinessById } = useBusiness()
const business = ref()

const { getAIPrompts } = useAI()
const aiOptions = ref([])

const setActiveAIPrompt = (aiOption: any) => {
  try {
    prompt.value = aiOption.prompt
    model.value = aiOption.model
    maxToken.value = aiOption.maxToken

    response.value = ''
    showAIModal.value = true
    op.value.hide()
  }
  catch (e) {
    console.log('error', e)
  }
}

const computedAIOptions = computed(() => {
  const replaceData = (prompt: string) => {
    return prompt
      .replace(/<[^>]*>?/gm, '')
      .replace(/\[WEBSITE_URL\]/g, business.value?.website ? `[${business.value.website}]` : '-')
      .replace(/\[BUSINESS_TEASER_NAME\]/g, business.value?.teaserTitle ? `[${business.value.teaserTitle}]` : '-')
      .replace(/\[BUSINESS_INDUSTRY\]/g, business.value?.industrySector ? `[${business.value.industrySector}]` : '-')
      .replace(/\[BUSINESS_INDUSTRY_SUB_SECTOR\]/g, business.value?.industrySubSector ? `[${business.value.industrySubSector}]` : '-')
      .replace(/\[BUSINESS_WEBSITE\]/g, business.value?.website ? `[${business.value.website}]` : '-')
      .replace(/\[BUSINESS_PROFIT\]/g, business.value?.profit ? `[${business.value.profit}]` : '-')
  }
  return aiOptions.value.map((option) => {
    return {
      ...option,
      prompt: replaceData(option.prompt),
    }
  })
})

const initAIPrompts = async () => {
  const res = await getAIPrompts()
  aiOptions.value = res.data
}
const { on, off } = useEventBus()

const eventBusHandler = (data) => {
  console.log('eventBusHandler', data)
  showAIModal.value = true
  const aiOption = computedAIOptions.value.find(option => option.type === data.type)
  setActiveAIPrompt(aiOption)
}
onMounted(async () => {
  const businessId = useRoute().params.businessId
  const res = await getBusinessById(businessId)
  business.value = {
    country: res?.IndustrySector?.country,
    industrySector: res?.IndustrySector?.name,
    industrySubSector: res?.IndustrySubSector?.name,
    profit: res?.profit,
    teaserTitle: res?.teaserTitle || '',
    website: res?.website || '',
  }

  await initAIPrompts()
  on(EventNames.RICH_TEXT_EDITOR_USE_AI_WIDGET, eventBusHandler)
})

onUnmounted(() => {
  off(EventNames.RICH_TEXT_EDITOR_USE_AI_WIDGET, eventBusHandler)
})
// const aiOptions = computed(() => aiOptionsData(useRoute().name?.toString(), business.value))
// const toggle = (event) => {
//   op.value.toggle(event)
// }

const { getAIResponse } = useAI()
const submit = async () => {
  isThinking.value = true

  const data = {
    maxToken: maxToken.value,
    model: model.value,
    prompt: prompt.value,
  }

  const res = await getAIResponse(data)

  //
  editor.value.commands.setContent(res?.data)
  data.value = res?.data

  isThinking.value = false
  prompt.value = ''
  response.value = ''
  showAIModal.value = false
}

function defineModel(arg0: string) {
  throw new Error('Function not implemented.')
}
</script>
